import { useEffect } from 'react';

import { Sheet } from '@repo/videa-components/src/index';
import { useAtom } from 'jotai';
import { useLocation } from 'react-router';

import { AIOppAvailableChart } from '../../charts/AIOppAvailableChart';
import { AIViewRateChart } from '../../charts/AIViewRateChart';
import { ConversionRateChart } from '../../charts/ConversionRateChart';
import { useGetFilteredClinicalData } from '../../helpers/data/getClinicalData';
import { useGetFilteredClinicalTreatments } from '../../helpers/data/getClinicalTreatments';
import { ProviderHeader } from '../ProviderHeader';

import { selectedInsightsPatientInfoAtom } from './atoms';
import { ClinicalProviderTable } from './ClinicalProviderTable';
import { PmsInfoSheet } from './PmsInfoSheet';

export const ProviderBenchmarks = () => {
  const [selectedInsightsPatientInfo, setSelectedInsightsPatientInfo] = useAtom(
    selectedInsightsPatientInfoAtom,
  );

  const location = useLocation();
  const id =
    location.pathname.split('provider/')[1].split('/')[0] === 'undefined'
      ? undefined
      : location.pathname.split('provider/')[1].split('/')[0];
  const practiceId =
    location.pathname.split('practice/')[1].split('/')[0] === 'undefined'
      ? undefined
      : location.pathname.split('practice/')[1].split('/')[0];

  const { data: clinicalData } = useGetFilteredClinicalData({
    isPracticesEnabled: false,
    isTreatmentsEnabled: false,
    isMaterialityEnabled: false,
    isProviderRoleEnabled: false,
  });

  const clinicalProvider = clinicalData?.practices
    ?.filter(
      (practice) =>
        !practiceId || practice?.practiceVideaId.toString() === practiceId,
    )
    ?.flatMap((practice) => {
      return practice?.providers?.map((provider) => {
        return {
          ...provider,
          practiceId: practice.practiceId,
          practiceVideaId: practice.practiceVideaId,
          practiceName: practice.practiceName,
        };
      });
    })
    .find(
      (provider) =>
        (provider?.providerVideaId ?? provider?.providerId.toString()) === id,
    );

  const { data: clinicalProviderData, isLoading: isClinicalLoading } =
    useGetFilteredClinicalTreatments({
      providerId: clinicalProvider?.providerId as number,
      practiceId: clinicalProvider?.practiceId,
    });

  useEffect(() => {
    return () => {
      setSelectedInsightsPatientInfo(undefined);
    };
  }, [setSelectedInsightsPatientInfo]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-8 vai-w-full vai-px-8 vai-py-4">
      <ProviderHeader
        singleProvider={{
          providerName: clinicalProvider?.providerName,
          providerVideaId: clinicalProvider?.providerVideaId,
          providerId: clinicalProvider?.providerId,
          practiceName: clinicalProvider?.practiceName,
          practiceId: clinicalProvider?.practiceVideaId,
          providerRole: clinicalProvider?.providerRole,
        }}
      />
      <div className="vai-flex vai-flex-row vai-justify-between vai-w-full vai-h-full">
        <div className="vai-flex vai-flex-col vai-w-[33%] vai-gap-4">
          <AIOppAvailableChart
            providers={
              clinicalProvider?.providerVideaId
                ? [clinicalProvider.providerVideaId]
                : undefined
            }
          />
          <AIViewRateChart
            providers={
              clinicalProvider?.providerVideaId
                ? [clinicalProvider.providerVideaId]
                : undefined
            }
          />
        </div>

        <ConversionRateChart
          level="Provider"
          providers={
            clinicalProvider?.providerVideaId
              ? [clinicalProvider.providerVideaId]
              : undefined
          }
        />
      </div>
      <div className="vai-flex vai-flex-row vai-justify-end">
        <Sheet
          open={!!selectedInsightsPatientInfo}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedInsightsPatientInfo(undefined);
            }
          }}
        >
          <ClinicalProviderTable
            treatments={clinicalProviderData}
            isLoading={isClinicalLoading}
            providerName={clinicalProvider?.providerName}
          />
          {selectedInsightsPatientInfo &&
            selectedInsightsPatientInfo.estimatedVisitId &&
            clinicalProvider?.practiceVideaId && (
              <PmsInfoSheet
                practiceId={clinicalProvider?.practiceVideaId}
                patientId={selectedInsightsPatientInfo.patientId}
                apptDate={selectedInsightsPatientInfo.apptDate}
                estimatedVisitId={selectedInsightsPatientInfo.estimatedVisitId}
              />
            )}
        </Sheet>
      </div>
    </div>
  );
};
